import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { CalendarCheck, ListTodo, AlarmClock, MessageCircleQuestion, PartyPopper, ChevronRight, Snail, TrendingUp, Hand, PhoneOutgoing, ShieldAlert } from 'lucide-react';

const CH = { letter: 'Letter', email: 'Email', linkedin: 'LinkedIn', call: 'Call' };
const SEC_TYPES = { rate_limit: 'Rate limit breaches', login_lockout: 'Login lockouts', account_locked: 'Accounts auto-locked', account_unlocked: 'Accounts unlocked' };
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { LATEST_VERSION } from '../lib/changelog';

// Phase G: Monday check-in popup — shows once per user per Monday (Berlin time).
// Append ?digest=force to the URL to preview it on any day.
export function MondayDigestModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    if (!user || user.role === 'ac_member') return;
    let timer = null;
    let openHandler = null;
    const forced = new URLSearchParams(window.location.search).get('digest') === 'force';
    const whatsNewPending = () => {
      try { return localStorage.getItem('olta-last-seen-version') !== LATEST_VERSION; } catch (e) { return false; }
    };
    api.getMondayDigest(forced)
      .then((res) => {
        if (!res.data?.should_show) return;
        setDigest(res.data);
        // Wait for the What's New modal to be dismissed before opening.
        openHandler = () => {
          if (whatsNewPending()) return;
          clearInterval(timer);
          window.removeEventListener('whats-new-dismissed', openHandler);
          setOpen(true);
        };
        window.addEventListener('whats-new-dismissed', openHandler);
        timer = setInterval(openHandler, 800);
        openHandler();
      })
      .catch(() => {});
    return () => {
      clearInterval(timer);
      if (openHandler) window.removeEventListener('whats-new-dismissed', openHandler);
    };
  }, [user]);

  const dismiss = () => {
    setOpen(false);
    api.markMondayDigestSeen().catch(() => {});
  };

  const goTo = (path) => {
    dismiss();
    navigate(path);
  };

  if (!digest) return null;
  const { tasks, sla, inactive } = digest;
  const slowFish = digest.slow_fish || [];
  const catches = digest.catches || [];
  const stuck = digest.stuck || [];
  const stuckTotal = stuck.reduce((n, g) => n + g.count, 0);
  const eng = digest.engagement;
  const fmtMin = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
  const outreach = digest.outreach;
  const oOverdue = outreach ? [...(outreach.overdue_calls || []), ...(outreach.overdue_other || [])] : [];
  const oDue = outreach?.due_today || [];
  const oBounced = outreach?.bounced || [];
  const sec = digest.security;
  const secLocked = sec?.locked || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="monday-digest-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-blue-400" /> Monday Check-in
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground pt-0.5">
            {digest.greeting_name ? `Good morning, ${digest.greeting_name} — ` : ''}{digest.date_label}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
          {catches.length > 0 && (
            <section data-testid="digest-catches-section">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-0.5">
                <PartyPopper className="w-4 h-4 text-emerald-400" /> Fresh Catches
                <span className="text-xs text-muted-foreground">{catches.length} deal{catches.length !== 1 ? 's' : ''} reached Closing</span>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Reeled in and on the hook — congratulations to the whole team! 🎉🎣
              </p>
              <ul className="space-y-1.5">
                {catches.map((c) => (
                  <li key={c.project_id}>
                    <button onClick={() => goTo(`/projects/${c.project_id}`)}
                      className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/20 transition-colors"
                      data-testid={`digest-catch-${c.project_id}`}>
                      <span className="text-foreground font-mono truncate">{c.deal_label}</span>
                      <span className="text-muted-foreground truncate">Closing{c.owner_name ? ` · ${c.owner_name}` : ''}</span>
                      {c.days_ago != null && (
                        <span className="ml-auto shrink-0 text-emerald-300">{c.days_ago < 1 ? 'today' : `${Math.round(c.days_ago)}d ago`}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {eng && (eng.scans > 0 || eng.contact_taps > 0 || eng.leads > 0) && (
            <section data-testid="digest-engagement-section">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-0.5">
                <TrendingUp className="w-4 h-4 text-sky-400" /> Engagement Week in Review
                <span className="text-xs text-muted-foreground">last 7 days</span>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                How entrepreneurs interacted with your landing pages this week.
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: eng.scans, l: `scan${eng.scans !== 1 ? 's' : ''}`, tid: 'eng-scans' },
                  { v: eng.avg_seconds > 0 ? `${fmtMin(eng.avg_seconds)} min` : '—', l: 'Ø time on page', tid: 'eng-avg-time' },
                  { v: eng.media_plays, l: `media play${eng.media_plays !== 1 ? 's' : ''}`, tid: 'eng-plays' },
                  { v: eng.contact_taps, l: `contact tap${eng.contact_taps !== 1 ? 's' : ''}`, tid: 'eng-taps', hot: eng.contact_taps > 0 },
                  { v: eng.leads, l: `lead${eng.leads !== 1 ? 's' : ''}`, tid: 'eng-leads', hot: eng.leads > 0 },
                  { v: eng.returning_visitors, l: `returning visitor${eng.returning_visitors !== 1 ? 's' : ''}`, tid: 'eng-returning', fire: eng.returning_visitors > 0 },
                ].map((s) => (
                  <div key={s.tid} data-testid={`digest-${s.tid}`}
                    className={`rounded-sm px-2 py-1.5 border text-center ${s.fire ? 'bg-orange-500/10 border-orange-500/25' : s.hot ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-sky-500/[0.06] border-sky-500/20'}`}>
                    <div className={`text-sm font-semibold ${s.fire ? 'text-orange-300' : s.hot ? 'text-emerald-300' : 'text-sky-300'}`}>{s.v}{s.fire ? ' 🔥' : ''}</div>
                    <div className="text-[10px] text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {sec && (sec.events_7d > 0 || secLocked.length > 0) && (
            <section data-testid="digest-security-section">
              <button onClick={() => goTo('/team')}
                className="w-full flex items-center justify-between mb-0.5 group"
                data-testid="digest-security-link">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ShieldAlert className={`w-4 h-4 ${secLocked.length > 0 ? 'text-red-400' : 'text-amber-400'}`} /> Security
                  <span className="text-xs text-muted-foreground">{sec.events_7d} event{sec.events_7d !== 1 ? 's' : ''} last 7 days</span>
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
              </button>
              <p className="text-[11px] text-muted-foreground mb-2">
                Rate-limit & login anomalies (admins only) — details in the Security Monitor on the Team page.
              </p>
              {secLocked.length > 0 && (
                <ul className="space-y-1.5 mb-2">
                  {secLocked.map((u) => (
                    <li key={u.id}>
                      <button onClick={() => goTo('/team')}
                        className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 transition-colors"
                        data-testid={`digest-security-locked-${u.id}`}>
                        <span className="text-foreground font-medium truncate">{u.name}</span>
                        <span className="text-muted-foreground truncate">{u.reason}</span>
                        <span className="ml-auto shrink-0 text-red-400 font-mono uppercase text-[10px]">locked</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-1.5" data-testid="digest-security-stats">
                {Object.entries(sec.by_type || {}).map(([t, n]) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-amber-500/10 border-amber-500/25 text-amber-300">
                    {SEC_TYPES[t] || t}: {n}
                  </span>
                ))}
                {(sec.ips || []).map((x) => (
                  <span key={x.ip} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border bg-secondary/60 border-border text-muted-foreground">
                    {x.ip} · {x.count}×
                  </span>
                ))}
              </div>
            </section>
          )}
          {digest.all_clear ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="digest-all-clear">
              <PartyPopper className="w-8 h-8 text-emerald-400" />
              <p className="text-sm text-foreground font-medium">Your pipeline is all clear</p>
              <p className="text-xs text-muted-foreground">No open tasks, SLA alerts or pending check-ins. Have a great week!</p>
            </div>
          ) : (
            <>
              {stuck.length > 0 && (
                <section data-testid="digest-stuck-section">
                  <button onClick={() => goTo('/whos-turn')}
                    className="w-full flex items-center justify-between mb-0.5 group"
                    data-testid="digest-stuck-link">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Hand className="w-4 h-4 text-red-400" /> Stuck Deals
                      <span className="text-xs text-red-400">{stuckTotal} waiting 7+ days</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                  </button>
                  <p className="text-[11px] text-muted-foreground mb-2">
                    Sitting with the same person for over a week — unblock or move them forward.
                  </p>
                  {stuck.map((g) => (
                    <div key={g.key} className="mb-2 last:mb-0">
                      <p className="text-[11px] font-medium text-red-400 mb-1">
                        Waiting on {g.name} · {g.count} deal{g.count !== 1 ? 's' : ''}
                      </p>
                      <ul className="space-y-1.5">
                        {g.deals.map((x) => (
                          <li key={`${g.key}-${x.project_id}`}>
                            <button onClick={() => goTo(`/projects/${x.project_id}`)}
                              className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 transition-colors"
                              data-testid={`digest-stuck-${x.project_id}`}>
                              <span className="text-foreground font-mono truncate">{x.deal_label}</span>
                              <span className="text-muted-foreground truncate">{x.action}</span>
                              <span className="ml-auto shrink-0 text-red-400 font-mono">{x.days}d</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>
              )}
              {(oOverdue.length > 0 || oDue.length > 0 || oBounced.length > 0) && (
                <section data-testid="digest-outreach-section">
                  <button onClick={() => goTo('/outreach')}
                    className="w-full flex items-center justify-between mb-0.5 group"
                    data-testid="digest-outreach-link">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <PhoneOutgoing className={`w-4 h-4 ${oOverdue.length > 0 ? 'text-red-400' : 'text-amber-400'}`} /> Outreach Health
                      <span className="text-xs text-muted-foreground">{outreach.active} active sequence{outreach.active !== 1 ? 's' : ''}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                  </button>
                  <p className="text-[11px] text-muted-foreground mb-2" data-testid="digest-outreach-summary">
                    {oDue.length > 0 && <span className="text-sky-300 font-medium">{oDue.length} touch{oDue.length !== 1 ? 'es' : ''} due today</span>}
                    {oDue.length > 0 && oOverdue.length > 0 && ' · '}
                    {oOverdue.length > 0 && <span className="text-red-400 font-medium">{oOverdue.length} overdue ({(outreach.overdue_calls || []).length} call{(outreach.overdue_calls || []).length !== 1 ? 's' : ''})</span>}
                    {(oDue.length > 0 || oOverdue.length > 0) && oBounced.length > 0 && ' · '}
                    {oBounced.length > 0 && <span className="text-orange-300 font-medium">{oBounced.length} bounce{oBounced.length !== 1 ? 's' : ''} (30d)</span>}
                  </p>
                  <ul className="space-y-1.5">
                    {oOverdue.slice(0, 5).map((x, i) => (
                      <li key={`o-over-${i}`}>
                        <button onClick={() => goTo(`/projects/${x.project_id}#section-outreach`)}
                          className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 transition-colors"
                          data-testid={`digest-outreach-overdue-${x.project_id}`}>
                          <span className="text-foreground truncate">{x.contact_name}</span>
                          <span className="text-muted-foreground truncate">{CH[x.channel] || x.channel}{x.fallback_from ? ` (statt ${CH[x.fallback_from]})` : ''}{x.project_name ? ` · ${x.project_name}` : ''}</span>
                          <span className="ml-auto shrink-0 text-red-400 font-mono">{x.days_over}d over</span>
                        </button>
                      </li>
                    ))}
                    {oDue.slice(0, 5).map((x, i) => (
                      <li key={`o-due-${i}`}>
                        <button onClick={() => goTo(`/projects/${x.project_id}#section-outreach`)}
                          className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-sky-500/10 border border-sky-500/25 hover:bg-sky-500/20 transition-colors"
                          data-testid={`digest-outreach-due-${x.project_id}`}>
                          <span className="text-foreground truncate">{x.contact_name}</span>
                          <span className="text-muted-foreground truncate">{CH[x.channel] || x.channel}{x.project_name ? ` · ${x.project_name}` : ''}</span>
                          <span className="ml-auto shrink-0 text-sky-300">today</span>
                        </button>
                      </li>
                    ))}
                    {oBounced.slice(0, 3).map((x, i) => (
                      <li key={`o-bounce-${i}`}>
                        <button onClick={() => goTo(`/projects/${x.project_id}#section-outreach`)}
                          className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-orange-500/10 border border-orange-500/25 hover:bg-orange-500/20 transition-colors"
                          data-testid={`digest-outreach-bounce-${x.project_id}`}>
                          <span className="text-foreground truncate">{x.contact_name}</span>
                          <span className="text-muted-foreground truncate">Email bounced{x.next_channel ? ` · next: ${CH[x.next_channel] || x.next_channel}` : ''}</span>
                          <span className="ml-auto shrink-0 text-orange-300 font-mono">{x.bounced_at}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {tasks.open > 0 && (
                <section data-testid="digest-tasks-section">
                  <button onClick={() => goTo('/tasks')}
                    className="w-full flex items-center justify-between mb-2 group"
                    data-testid="digest-tasks-link">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <ListTodo className="w-4 h-4 text-blue-400" /> Open Tasks
                      <span className="text-xs text-muted-foreground">
                        {tasks.open}{tasks.overdue > 0 && <span className="text-red-400"> · {tasks.overdue} overdue</span>}
                        {tasks.due_today > 0 && <span className="text-amber-400"> · {tasks.due_today} due today</span>}
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                  </button>
                  <ul className="space-y-1.5">
                    {tasks.items.map((t) => (
                      <li key={t.id}>
                        <button onClick={() => goTo(`/projects/${t.project_id}`)}
                          className="w-full text-left flex items-baseline gap-2 text-xs rounded-sm px-2 py-1.5 bg-secondary/40 hover:bg-secondary/70 transition-colors"
                          data-testid={`digest-task-${t.id}`}>
                          <span className={`shrink-0 w-1.5 h-1.5 rounded-full mt-1 ${t.overdue ? 'bg-red-400' : t.due_today ? 'bg-amber-400' : 'bg-blue-400'}`} />
                          <span className="text-foreground truncate">{t.title}</span>
                          <span className="ml-auto shrink-0 text-muted-foreground font-mono">{t.deal_label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(sla.breached.length > 0 || sla.warning.length > 0) && (
                <section data-testid="digest-sla-section">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                    <AlarmClock className="w-4 h-4 text-red-400" /> SLA Alerts
                    <span className="text-xs text-muted-foreground">
                      {sla.breached_count > 0 && <span className="text-red-400">{sla.breached_count} breached</span>}
                      {sla.breached_count > 0 && sla.warning_count > 0 && ' · '}
                      {sla.warning_count > 0 && <span className="text-amber-400">{sla.warning_count} expiring</span>}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {[...sla.breached.map((e) => ({ ...e, kind: 'breached' })),
                      ...sla.warning.map((e) => ({ ...e, kind: 'warning' }))].map((e, i) => (
                      <li key={`${e.project_id}-${i}`}>
                        <button onClick={() => goTo(`/projects/${e.project_id}`)}
                          className={`w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 border transition-colors ${e.kind === 'breached' ? 'bg-red-500/10 border-red-500/25 hover:bg-red-500/20' : 'bg-amber-500/10 border-amber-500/25 hover:bg-amber-500/20'}`}
                          data-testid={`digest-sla-${e.project_id}`}>
                          <span className="text-foreground font-mono truncate">{e.deal_label}</span>
                          <span className="text-muted-foreground truncate">{e.stage_label}</span>
                          <span className={`ml-auto shrink-0 ${e.kind === 'breached' ? 'text-red-400' : 'text-amber-400'}`}>
                            {e.days_left != null && e.days_left < 0 ? `${Math.abs(Math.round(e.days_left))}d over` : e.kind === 'breached' ? 'breached' : 'soon'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {slowFish.length > 0 && (
                <section data-testid="digest-slowfish-section">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-0.5">
                    <Snail className="w-4 h-4 text-sky-400" /> Dragging Along Too Long
                    <span className="text-xs text-muted-foreground">{slowFish.length} {slowFish.length === 1 ? 'deal' : 'deals'}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">
                    These deals have been in their stage longer than the team usually needs — push them forward or cut them loose. 🐌
                  </p>
                  <ul className="space-y-1.5">
                    {slowFish.map((f) => (
                      <li key={f.project_id}>
                        <button onClick={() => goTo(`/projects/${f.project_id}`)}
                          className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-sky-500/10 border border-sky-500/25 hover:bg-sky-500/20 transition-colors"
                          data-testid={`digest-slowfish-${f.project_id}`}>
                          <span className="text-foreground font-mono truncate">{f.deal_label}</span>
                          <span className="text-muted-foreground truncate">{f.stage_label}{f.owner_name ? ` · ${f.owner_name}` : ''}</span>
                          <span className="ml-auto shrink-0 text-sky-300 font-mono">
                            {Math.round(f.days_in_stage)}d vs Ø {f.avg_days}d
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {inactive.length > 0 && (
                <section data-testid="digest-inactive-section">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                    <MessageCircleQuestion className="w-4 h-4 text-purple-400" /> Check-ins Pending
                    <span className="text-xs text-muted-foreground">{inactive.length} deal{inactive.length !== 1 ? 's' : ''} awaiting an update</span>
                  </div>
                  <ul className="space-y-1.5">
                    {inactive.map((d) => (
                      <li key={d.project_id}>
                        <button onClick={() => goTo(`/projects/${d.project_id}`)}
                          className="w-full text-left flex items-center gap-2 text-xs rounded-sm px-2 py-1.5 bg-secondary/40 hover:bg-secondary/70 transition-colors"
                          data-testid={`digest-inactive-${d.project_id}`}>
                          <span className="text-foreground font-mono truncate">{d.deal_label}</span>
                          <span className="text-muted-foreground truncate">{d.stage_label}{d.owner_name ? ` · ${d.owner_name}` : ''}</span>
                          {d.days_waiting != null && (
                            <span className="ml-auto shrink-0 text-purple-300">{Math.round(d.days_waiting)}d waiting</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={dismiss} data-testid="monday-digest-dismiss"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9 px-4">
            Start the week
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
